import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Shield, ArrowRight, Database, Video,
  Lock, Globe, FileCheck, UserCheck, Zap, Send, Menu, X,
  BarChart3
} from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";
import partnerHeroBg from "@assets/create_a_logo_for_Vexis_,_using_it_as_the_primary_focal_point._1775281077354.jpg";

export default function Partners() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Partners & Integrations | Vexis";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      {/* Navigation */}
      <nav
        className="sticky top-0 z-50"
        style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}
      >
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
            <span className="font-bold tracking-widest text-sm text-white">VEXIS</span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <Link href="/how-it-works" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">How It Works</Link>
            <Link href="/pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Pricing</Link>
            <Link href="/partners" className="text-sm font-medium text-white transition-colors">Partners</Link>
            <Link href="/contact" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Contact</Link>
            <Link href="/about" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">About</Link>
          </div>
          <div className="hidden lg:flex gap-3">
            <Link href="/contact">
              <Button className="font-semibold" style={{ backgroundColor: "#6366f1", color: "#ffffff" }} data-testid="button-partners-enquiry">
                Partner enquiry
              </Button>
            </Link>
          </div>
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden" style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }}>
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <Link href="/how-it-works" className="text-sm font-medium text-slate-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
              <Link href="/pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link href="/partners" className="text-sm font-medium text-white py-2" onClick={() => setMobileMenuOpen(false)}>Partners</Link>
              <Link href="/contact" className="text-sm font-medium text-slate-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link href="/about" className="text-sm font-medium text-slate-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <div className="flex flex-col gap-2 pt-4" style={{ borderTop: "1px solid rgba(99,102,241,0.15)" }}>
                <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full font-semibold" style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>Partner enquiry</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section
        className="relative py-16 md:py-28 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${partnerHeroBg})` }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-slate-400 uppercase tracking-widest text-xs mb-5 font-semibold">ATS Platform Partnerships</p>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white mb-6">
              Keep your recruiters in your platform. Give them an assessment capability they cannot get anywhere else.
            </h1>
            <p className="text-base md:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Vexis connects to ATS platforms via OAuth to add structured video interviews, AI triage, and human-reviewed candidate dossiers to your recruiter workflow. Your users get an assessment layer that produces results their clients trust. You get a more complete platform that is harder to leave.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a href="mailto:api@vexistalentagency.com?subject=Partnership%20enquiry">
                <Button size="lg" className="h-12 px-8 font-semibold w-full sm:w-auto" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} data-testid="button-hero-enquiry">
                  Submit a partnership enquiry
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link href="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 w-full sm:w-auto" data-testid="button-hero-api-docs">
                  Request API documentation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Security section */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-slate-500 uppercase tracking-widest text-xs mb-3 font-semibold">Compliance</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Enterprise security and compliance
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {[
                {
                  icon: Database,
                  text: "Candidate data stored on Supabase - AWS ap-southeast-1 Singapore region - SOC 2 Type 2 certified",
                },
                {
                  icon: Video,
                  text: "Video processed by Cloudflare Stream - never stored on Vexis servers - auto-deleted within 24 hours of assessment completion",
                },
                {
                  icon: Shield,
                  text: "GDPR compliant - candidate data deletion actioned within 30 days on written request",
                },
                {
                  icon: Lock,
                  text: "OAuth tokens stored in environment secrets only - never in database - minimum scope access: read, write, offline_access, partner_ui_action",
                },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-start gap-4 p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(99,102,241,0.18)" }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(99,102,241,0.2)" }}>
                    <Icon className="w-5 h-5 text-indigo-400" />
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What recruiters get */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-slate-500 uppercase tracking-widest text-xs mb-3 font-semibold">Recruiter Value</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                What Vexis adds to every candidate record in your platform
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: <Zap className="w-5 h-5 text-indigo-400" />,
                  text: "A Send to Vexis Assessment action button on every candidate record - one click starts the full assessment workflow",
                },
                {
                  icon: <Send className="w-5 h-5 text-indigo-400" />,
                  text: "A personalised video interview link dispatched to the candidate by email and SMS automatically - no scheduling required",
                },
                {
                  icon: <BarChart3 className="w-5 h-5 text-indigo-400" />,
                  text: "AI triage banding within under 2 minutes of candidate submission - Green, Amber, or Red - so recruiters know where to focus",
                },
                {
                  icon: <UserCheck className="w-5 h-5 text-indigo-400" />,
                  text: "Human-reviewed assessment scores and written notes written back to the candidate record in your ATS",
                },
                {
                  icon: <FileCheck className="w-5 h-5 text-indigo-400" />,
                  text: "A secure, shareable dossier URL stored against the candidate record - ready to send to the hiring client",
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.18)" }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: "rgba(99,102,241,0.15)" }}>
                    {item.icon}
                  </div>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed pt-1">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration architecture */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-slate-500 uppercase tracking-widest text-xs mb-3 font-semibold">Technical</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Integration architecture
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Globe,
                  title: "OAuth2 connection",
                  desc: "Recruiters connect Vexis in under 2 minutes using a standard OAuth2 flow. We request only the minimum scopes required. No admin intervention. No IT dependency.",
                },
                {
                  icon: Zap,
                  title: "Native action button",
                  desc: "A Send to Vexis Assessment action button appears on candidate records in your platform. Clicking it opens the Vexis assessment panel inside your interface. The recruiter never leaves your product.",
                },
                {
                  icon: FileCheck,
                  title: "Automatic write-back",
                  desc: "When the assessment is complete, Vexis writes the result back to the candidate record: stage update, score and recommendation note, and the dossier URL. Your platform data stays current without any manual step.",
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="rounded-xl p-7 hover:border-indigo-500/40 transition-all"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.18)" }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(99,102,241,0.2)" }}>
                    <Icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-white">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Partners */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-slate-500 uppercase tracking-widest text-xs mb-3 font-semibold">Integration Partners</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Live integrations
              </h2>
            </div>

            {/* JobAdder partner card */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(59,130,246,0.35)" }}>
              {/* Card header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-7 py-6"
                style={{ borderBottom: "1px solid rgba(59,130,246,0.18)", background: "rgba(59,130,246,0.06)" }}>
                <div className="flex items-center gap-4">
                  <img
                    src="/images/jobadder-logo.jpg"
                    alt="JobAdder"
                    className="h-14 w-14 rounded-xl object-cover flex-shrink-0"
                    style={{ boxShadow: "0 0 0 1px rgba(59,130,246,0.4)" }}
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white">JobAdder</h3>
                    <p className="text-sm" style={{ color: "#94a3b8" }}>Applicant Tracking System</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)" }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#22C55E" }} />
                  <span className="text-xs font-semibold" style={{ color: "#22C55E" }}>Live — Official Partner</span>
                </div>
              </div>

              {/* Card body */}
              <div className="px-7 py-7 grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#818cf8" }}>About the Integration</h4>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Vexis is a certified JobAdder Marketplace partner. The integration surfaces directly inside the JobAdder recruiter interface — no tab switching, no new logins, no workflow changes required.
                  </p>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    A <strong className="text-white">Send to Vexis Assessment</strong> action button appears on every candidate record. One click dispatches a structured video interview link to the candidate by email and SMS. When the assessment is complete, the AI triage score, written recommendation, and secure dossier link are written back to the candidate record automatically.
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    JobAdder recruiters can activate the integration in under two minutes using a standard OAuth2 connection — no IT involvement, no API keys to manage.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#818cf8" }}>What's Included</h4>
                  <ul className="space-y-3">
                    {[
                      "Native action button on every candidate record",
                      "Automated interview dispatch by email and SMS",
                      "AI triage banding: Green, Amber, or Red within minutes",
                      "Human-reviewed score and recommendation note written back to the ATS",
                      "Secure, shareable dossier URL stored against the candidate record",
                      "Pipeline stage sync — assessment progress reflected in JobAdder",
                      "OAuth2 connection — set up in under 2 minutes",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: "#3b82f6" }} />
                        <span className="text-slate-300 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Card footer */}
              <div className="px-7 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                style={{ borderTop: "1px solid rgba(59,130,246,0.12)", background: "rgba(59,130,246,0.04)" }}>
                <p className="text-xs" style={{ color: "#64748b" }}>
                  Available to all Vexis Starter, Growth, Agency, and Enterprise subscribers.
                </p>
                <Link href="/register">
                  <Button size="sm" className="font-semibold flex-shrink-0"
                    style={{ backgroundColor: "#3b82f6", color: "#ffffff" }}>
                    Connect JobAdder now
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration availability */}
      <section className="py-12 md:py-20" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-slate-500 uppercase tracking-widest text-xs mb-3 font-semibold">Coming Soon</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                More integrations in development
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.18)" }}>
                <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white mb-0.5">Additional ATS platforms</p>
                  <p className="text-slate-400 text-sm">Any OAuth2-compatible ATS or CRM can integrate via our open API. Contact us to discuss your platform — we are actively building new integrations and prioritise based on partner demand.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-12 md:py-24 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-slate-400 uppercase tracking-widest text-xs mb-4 font-semibold">Get in touch</p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-5">
              Interested in partnering with Vexis?
            </h2>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
              Vexis is built to integrate with any ATS, CRM, or recruitment platform via OAuth2. If you represent a recruitment platform and want to offer your users a structured assessment capability — without building it yourself — we would like to hear from you. Adding Vexis to your platform is a single OAuth connection for your recruiters and a native action button on every candidate record.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href="mailto:api@vexistalentagency.com?subject=Partnership%20enquiry">
                <Button size="lg" className="h-12 px-8 font-semibold w-full sm:w-auto" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} data-testid="button-cta-enquiry">
                  Submit a partnership enquiry
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link href="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 w-full sm:w-auto" data-testid="button-cta-api-docs">
                  Request API documentation
                </Button>
              </Link>
            </div>

            <p className="text-slate-300 text-sm">
              Or email us directly at{" "}
              <a
                href="mailto:api@vexistalentagency.com"
                className="text-white font-semibold underline underline-offset-2 hover:text-slate-200 transition-colors"
                data-testid="link-partner-email"
              >
                api@vexistalentagency.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-10" style={{ backgroundColor: "#020408", borderTop: "1px solid rgba(99,102,241,0.15)" }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 w-auto mb-3" />
              <p className="text-slate-400 text-sm">
                LHA Holdings Ltd trading as Vexis Talent Agency
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
              <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
          <div className="mt-8 pt-6 text-slate-500 text-xs text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            &copy; 2026 Vexis Talent Agency. A trading name of LHA Holdings Ltd.
          </div>
        </div>
      </footer>
    </div>
  );
}
